import {
  extractContentContestIds,
  useActiveContentContestIdsDispatch,
} from '@/contexts/ActiveContentContestIdsProvider';
import { RelayType } from '@diritto/common/content/constants';
import { ContestId } from '@diritto/common/content/contest.constants';
import { ContentRelayDto } from '@diritto/common/relay/dtos/content-relay.dtos';
import { ProfileLiteRelayDto } from '@diritto/common/relay/dtos/profile-lite-relay.dtos';
import { accurateItems, flatItems } from '@diritto/common/utils/array';
import styled from '@emotion/styled';
import { ProfileContentItem } from 'app-components/accounts/profiles/ProfileContentItem';
import { useAuthState } from 'contexts/AuthProvider';
import { useGetRelayProfileContentsInfiniteQuery } from 'queries/contents/useGetRelayProfileContentsInfiniteQuery';
import { useEffect, useMemo } from 'react';
import { EmptyList } from 'ui-components/common/EmptyList';
import { InfiniteScrollWrapper } from 'ui-components/common/InfiniteScrollWrapper';
import { Loading } from 'ui-components/common/Loading';
import { ContentBodyPaddingContainer } from 'ui-components/layout/containers/ContentBodyCenterContainer';

interface ProfileContentListProps {
  profileId: number;
}

export const ProfileContentList = ({ profileId }: ProfileContentListProps) => {
  const { includeAdult } = useAuthState();
  const { checkActiveContentContests } = useActiveContentContestIdsDispatch();

  const {
    data: contentsData,
    hasNextPage,
    fetchNextPage,
    isInitialLoading,
  } = useGetRelayProfileContentsInfiniteQuery(profileId, { includeAdult });

  const { contentIds, contents, profiles } = useMemo(
    () => ({
      contentIds: flatItems<number>('contentIds', contentsData?.pages) ?? [],
      contents: accurateItems<ContentRelayDto>('contents', contentsData?.pages),
      profiles: accurateItems<ProfileLiteRelayDto>('profileLites', contentsData?.pages) ?? {},
    }),
    [contentsData?.pages],
  );

  useEffect(() => {
    if (contents && contentIds.length > 0) {
      const contentContestIds = extractContentContestIds(contentIds, contents);
      checkActiveContentContests(ContestId.Contest2026, contentContestIds);
    }
  }, [contents, contentIds, checkActiveContentContests]);

  return (
    <ContentBodyPaddingContainer>
      <InfiniteScrollWrapper hasNext={hasNextPage} loadMore={fetchNextPage}>
        {!isInitialLoading && contentIds.length === 0 && <EmptyList description="작품이 없어요." />}
        <StyledList>
          {contentIds.map(id => {
            const content = contents[id];
            if (content.relayType !== RelayType.Public) {
              return null;
            }
            const author = content.profileId && profiles[content.profileId];
            if (!author || author.isDeleted) {
              return null;
            }

            return <ProfileContentItem key={id} content={content} profile={author} />;
          })}
          {isInitialLoading && <Loading />}
        </StyledList>
      </InfiniteScrollWrapper>
    </ContentBodyPaddingContainer>
  );
};

const StyledList = styled.div`
  display: flex;
  flex-direction: column;
  padding: 16px 0;
  gap: 8px;
`;
